import type { AssistanceService } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

export type ConversationHydrationCoordinator = Readonly<{
	scopeKey: string;
	getPending: (conversationId: string) => Promise<boolean> | undefined;
	setPending: (conversationId: string, request: Promise<boolean>) => void;
	clearPending: (conversationId: string, request: Promise<boolean>) => void;
	wasRemovedWhilePending: (conversationId: string) => boolean;
	markRemoved: (conversationId: string) => void;
}>;

const createConversationHydrationCoordinator = (
	scopeKey: string,
): ConversationHydrationCoordinator => {
	const pending = new Map<string, Promise<boolean>>();
	const removedWhilePending = new Set<string>();

	return {
		scopeKey,
		getPending: (conversationId) => pending.get(conversationId),
		setPending: (conversationId, request) => pending.set(conversationId, request),
		clearPending: (conversationId, request) => {
			if (pending.get(conversationId) === request) {
				pending.delete(conversationId);
				removedWhilePending.delete(conversationId);
			}
		},
		wasRemovedWhilePending: (conversationId) => removedWhilePending.has(conversationId),
		markRemoved: (conversationId) => {
			if (pending.has(conversationId)) {
				removedWhilePending.add(conversationId);
			}
		},
	};
};

const coordinators = new WeakMap<
	AssistanceService,
	Map<string, ConversationHydrationCoordinator>
>();

/** Shares pending work across every chat-store consumer for one service and identity scope. */
export const getConversationHydrationCoordinator = (
	assistanceService: AssistanceService,
	scopeKey: string,
): ConversationHydrationCoordinator => {
	let scopedCoordinators = coordinators.get(assistanceService);
	if (scopedCoordinators == null) {
		scopedCoordinators = new Map();
		coordinators.set(assistanceService, scopedCoordinators);
	}
	const coordinator =
		scopedCoordinators.get(scopeKey) ?? createConversationHydrationCoordinator(scopeKey);
	scopedCoordinators.set(scopeKey, coordinator);
	return coordinator;
};
